// Engine heartbeat in the top bar: is the engine that runs your requests online? It distinguishes "the engine has not run
// your request yet" from "the engine is offline" (DATA_CONTRACT §1 defect 6). Freshness thresholds are display choices only.
import { useEffect, useState } from "react";
import { StatusPill } from "../components";
import type { Heartbeat } from "../data/types";
import { ago, fmtStamp } from "../lib/format";
import { useLoad, useRealtime } from "../lib/hooks";
import { useApp } from "./context";

export const ONLINE_SECONDS = 90;      // the loop beats every 30 s
export const STALE_SECONDS = 600;

export function engineState(beats: Heartbeat[], mode: "demo" | "supabase" | "local", now = Date.now()): { state: string; label: string; detail: string } {
  if (mode === "demo") return { state: "neutral", label: "Demo data", detail: "No engine behind the demo: requests stay waiting." };
  if (!beats.length) return { state: "blocked", label: "Engine never connected", detail: "No heartbeat published. Run yvc cloud sync --watch on the engine's machine." };
  const b = [...beats].sort((x, y) => y.lastSeen.localeCompare(x.lastSeen))[0];
  const age = (now - new Date(b.lastSeen).getTime()) / 1000;
  const who = `${b.holderName}'s engine (${b.buildVersion})`;
  if (age <= ONLINE_SECONDS) return { state: "done", label: "Engine online", detail: `${who}, last beat ${ago(b.lastSeen, now)}; ${b.inboxPending} request(s) waiting.` };
  if (age <= STALE_SECONDS) return { state: "waiting", label: "Engine slow", detail: `${who}, last beat ${ago(b.lastSeen, now)}.` };
  return { state: "blocked", label: "Engine offline", detail: `${who}, last beat ${fmtStamp(b.lastSeen)}. Requests wait until it is back.` };
}

export function EngineStatus() {
  const { ds } = useApp();
  const hb = useLoad(() => ds.heartbeat(), [ds]);
  useRealtime(ds, "heartbeat", hb.reload);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 15_000); return () => clearInterval(t); }, []);
  const s = hb.error ? { state: "blocked", label: "Engine status unknown", detail: hb.error } : engineState(hb.data ?? [], ds.mode, now);
  return (
    <span className="hidden items-center sm:inline-flex" title={s.detail} aria-label={`${s.label}. ${s.detail}`} role="status">
      <StatusPill state={s.state === "neutral" ? "info" : s.state} kind={s.state === "neutral" ? "neutral" : undefined} label={s.label} />
    </span>
  );
}
